/**
 * React Query Configuration
 * 
 * Centralized configuration for TanStack Query (React Query)
 * Handles caching, refetching, and error handling for all API calls
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000,
      
      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      
      // Retry failed requests once
      retry: 1,
      
      // Refetch on window focus for fresh data
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Don't retry failed mutations to prevent duplicate non-idempotent operations
      retry: false,
    },
  },
});

/**
 * Query Keys Factory
 * 
 * Centralized query key management for consistent caching
 * and easy invalidation across the app
 */
export const queryKeys = {
  // Programs
  programs: {
    all: ['programs'] as const,
    lists: () => [...queryKeys.programs.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.programs.lists(), filters] as const,
    details: () => [...queryKeys.programs.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.programs.details(), id] as const,
    admins: (programId: string) => [...queryKeys.programs.detail(programId), 'admins'] as const,
  },
  
  // Cohorts
  cohorts: {
    all: ['cohorts'] as const,
    lists: () => [...queryKeys.cohorts.all, 'list'] as const,
    list: (programId?: string) => [...queryKeys.cohorts.lists(), programId] as const,
    details: () => [...queryKeys.cohorts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.cohorts.details(), id] as const,
    enrollments: (cohortId: string) => [...queryKeys.cohorts.detail(cohortId), 'enrollments'] as const,
    participants: (cohortId: string, statusFilter?: string) => [...queryKeys.cohorts.detail(cohortId), 'participants', statusFilter] as const,
    progress: (cohortId: string) => [...queryKeys.cohorts.detail(cohortId), 'progress'] as const,
    userCompletions: (cohortId: string, userId: string) => [...queryKeys.cohorts.detail(cohortId), 'users', userId, 'completions'] as const,
    graduationRequests: (cohortId: string) => [...queryKeys.cohorts.detail(cohortId), 'graduation-requests'] as const,
    actionItems: (cohortId: string) => [...queryKeys.cohorts.detail(cohortId), 'action-items'] as const,
  },
  
  // Mentors
  mentors: {
    all: ['mentors'] as const,
    lists: () => [...queryKeys.mentors.all, 'list'] as const,
    list: (cohortId: string) => [...queryKeys.mentors.lists(), cohortId] as const,
    requests: (cohortId: string) => [...queryKeys.mentors.all, 'requests', cohortId] as const,
    forUser: (userId: string, cohortId: string) => [...queryKeys.mentors.all, 'user', userId, cohortId] as const,
    mine: (cohortId: string) => [...queryKeys.mentors.all, 'mine', cohortId] as const,
    mentees: (cohortId: string, mentorId: string) => [...queryKeys.mentors.all, 'mentees', cohortId, mentorId] as const,
  },
  
  // Activities
  activities: {
    all: ['activities'] as const,
    lists: () => [...queryKeys.activities.all, 'list'] as const,
    list: (cohortId: string) => [...queryKeys.activities.lists(), cohortId] as const,
    completions: (userId: string, cohortId: string) => [...queryKeys.activities.all, 'completions', userId, cohortId] as const,
  },
  
  // Users
  users: {
    all: ['users'] as const,
    dashboard: (userId: string) => [...queryKeys.users.all, 'dashboard', userId] as const,
    permissions: (userId: string) => [...queryKeys.users.all, 'permissions', userId] as const,
    directReports: (userId: string) => [...queryKeys.users.all, 'direct-reports', userId] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    list: () => [...queryKeys.notifications.all, 'list'] as const,
  },
};
